import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Camera, Heart, Upload, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfileSetup = () => {
  const [step, setStep] = useState(1);
  const [relationshipStartDate, setRelationshipStartDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [crop, setCrop] = useState({ unit: '%', width: 80, aspect: 1 });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const imgRef = useRef(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const { user, login } = useAuth();
  
  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Image must be less than 10MB');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setSelectedImage(reader.result);
        setShowCropDialog(true);
      });
      reader.readAsDataURL(file);
    }
  };
  
  const onImageLoad = useCallback((img) => {
    imgRef.current = img;
    const width = img.width > img.height ? (img.height / img.width) * 80 : 80;
    const height = img.height > img.width ? (img.width / img.height) * 80 : 80;
    const x = (100 - width) / 2;
    const y = (100 - height) / 2;
    
    setCrop({
      unit: '%',
      width,
      height,
      x,
      y,
      aspect: 1
    });
    return false;
  }, []);
  
  const getCroppedImg = async () => {
    if (!completedCrop || !imgRef.current) return null;
    
    const canvas = document.createElement('canvas');
    const image = imgRef.current;
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    
    // Limit output size for mobile
    const maxSize = 400;
    const outputWidth = Math.min(completedCrop.width * scaleX, maxSize);
    const outputHeight = Math.min(completedCrop.height * scaleY, maxSize);
    
    canvas.width = outputWidth;
    canvas.height = outputHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      outputWidth,
      outputHeight
    );
    
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/jpeg', 0.8);
    });
  };
  
  const handleSaveCrop = async () => {
    try {
      const croppedBlob = await getCroppedImg();
      if (!croppedBlob) {
        toast.error('Failed to crop image');
        return;
      }
      
      // Convert to base64
      const reader = new FileReader();
      reader.readAsDataURL(croppedBlob);
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
        setShowCropDialog(false);
        setSelectedImage(null);
        toast.success('Photo ready!');
      };
    } catch (error) {
      toast.error('Failed to process image');
    }
  };
  
  const handlePictureSubmit = async () => {
    if (!previewUrl) {
      toast.error('Please select a profile picture');
      return;
    }
    
    setLoading(true);
    try {
      await axios.put(
        `${API}/user/picture`,
        { picture_data: previewUrl },
        { withCredentials: true }
      );
      
      const updatedUser = { ...user, picture: previewUrl };
      login(updatedUser);
      setStep(2);
    } catch (error) {
      console.error('Picture save error:', error);
      toast.error('Failed to save picture');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDateSubmit = async () => {
    if (!relationshipStartDate) {
      toast.error('Please select your relationship start date');
      return;
    }
    
    // Validate date is not in future
    const selected = new Date(relationshipStartDate);
    const today = new Date();
    if (selected > today) {
      toast.error('Date cannot be in the future');
      return;
    }
    
    setLoading(true);
    try {
      await axios.put(
        `${API}/user/relationship-start`,
        { date: relationshipStartDate },
        { withCredentials: true, params: { date: relationshipStartDate } }
      );
      
      const updatedUser = { ...user, relationship_start_date: relationshipStartDate };
      login(updatedUser);
      navigate('/connection');
    } catch (error) {
      toast.error('Failed to save date');
    } finally {
      setLoading(false);
    }
  };
  
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };
  
  // Step 1: Profile Picture
  if (step === 1) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-white">
        <div className="w-full max-w-sm space-y-8">
          {/* Logo */}
          <div className="flex justify-center">
            <img 
              src="/couplein_logo.png"
              alt="CoupleIn"
              className="h-20"
            />
          </div>
          
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900">Add Your Photo</h1>
            <p className="text-gray-500 mt-2">Let your partner see your smile</p>
          </div>
          
          {/* Profile Picture Preview */}
          <div className="flex justify-center">
            <div className="relative">
              {previewUrl ? (
                <img 
                  src={previewUrl} 
                  alt="Preview" 
                  className="w-32 h-32 rounded-full object-cover border-4 border-primary/20"
                />
              ) : (
                <div className="w-32 h-32 rounded-full bg-gray-100 flex items-center justify-center border-4 border-dashed border-gray-300">
                  <Camera className="w-10 h-10 text-gray-400" />
                </div>
              )}
              <button 
                onClick={() => fileInputRef.current?.click()}
                className="absolute -bottom-2 -right-2 w-10 h-10 bg-primary rounded-full flex items-center justify-center shadow-pink border-2 border-white"
              >
                <Upload className="w-5 h-5 text-white" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          </div>
          
          <Button
            onClick={handlePictureSubmit}
            disabled={loading || !previewUrl}
            className="w-full h-14 rounded-2xl gradient-brand shadow-pink font-semibold text-white"
          >
            {loading ? 'Saving...' : 'Continue'}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          
          <button
            onClick={() => setStep(2)}
            className="w-full text-center text-gray-400 text-sm"
          >
            Skip for now
          </button>
        </div>
        
        {/* Crop Dialog */}
        <Dialog open={showCropDialog} onOpenChange={setShowCropDialog}>
          <DialogContent className="max-w-sm rounded-2xl">
            <DialogHeader>
              <DialogTitle>Crop Your Photo</DialogTitle>
            </DialogHeader>
            
            {/* Important instruction for mobile users */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-2">
              <p className="text-amber-800 text-sm font-medium text-center">
                📱 Drag to adjust crop area, then tap Save
              </p>
            </div>
            
            <div className="flex justify-center py-2">
              {selectedImage && (
                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={1}
                  circularCrop
                >
                  <img
                    ref={imgRef}
                    src={selectedImage}
                    alt="Crop"
                    onLoad={(e) => onImageLoad(e.currentTarget)}
                    style={{ maxHeight: '280px' }}
                  />
                </ReactCrop>
              )}
            </div>
            <DialogFooter className="flex gap-3">
              <Button
                onClick={() => {
                  setShowCropDialog(false);
                  setSelectedImage(null);
                }}
                variant="outline"
                className="flex-1 h-11 rounded-xl"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveCrop}
                className="flex-1 h-11 rounded-xl gradient-brand text-white"
              >
                Save Photo
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }
  
  // Step 2: Relationship Start Date
  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-white">
      <div className="w-full max-w-sm space-y-8">
        {/* Logo */}
        <div className="flex justify-center">
          <img 
            src="/couplein_logo.png"
            alt="CoupleIn"
            className="h-20"
          />
        </div>
        
        <div className="text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-full mx-auto flex items-center justify-center mb-4">
            <Heart className="w-8 h-8 text-primary" fill="currentColor" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">When Did It Begin?</h1>
          <p className="text-gray-500 mt-2">We'll count every day together</p>
        </div>
        
        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium text-gray-700">Relationship Start Date</Label>
            <Input
              type="date"
              value={relationshipStartDate}
              onChange={(e) => setRelationshipStartDate(e.target.value)}
              max={getTodayDate()}
              className="mt-2 h-12 rounded-xl"
            />
          </div>
        </div>
        
        <Button
          onClick={handleDateSubmit}
          disabled={loading || !relationshipStartDate}
          className="w-full h-14 rounded-2xl gradient-brand shadow-pink font-semibold text-white"
        >
          {loading ? 'Saving...' : 'Continue'}
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
        
        <button
          onClick={() => navigate('/connection')}
          className="w-full text-center text-gray-400 text-sm"
        >
          Skip for now
        </button>
      </div>
    </div>
  );
};

export default ProfileSetup;
